import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ArrowRight, ClipboardList, FilePlus2, Inbox } from 'lucide-react';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import QueryErrorBanner from '../../components/Common/QueryErrorBanner';
import { getAppHomePath } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { identityService, licenseService, processService } from '../../services/api';
import { isRequesterRole } from '../../utils/tenantProfile';
import {
  FILA_ORGAO,
  checklistProgress,
  formatReqDate,
  requerimentoStatusMeta,
} from '../../utils/requerimentoStatus';
import { fetchAllPages } from '../../utils/apiPagination';

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? payload?.items ?? [];
}

const FILTERS_REQUESTER = [
  { id: 'todos', label: 'Todos' },
  { id: 'rascunho', label: 'Rascunhos' },
  { id: 'enviado', label: 'Em análise' },
  { id: 'devolvido', label: 'Devolvidos' },
  { id: 'processo_aberto', label: 'Processo aberto' },
];

const FILTERS_ORGAO = [
  { id: 'fila', label: 'Fila do órgão' },
  { id: 'devolvido', label: 'Devolvidos' },
  { id: 'processo_aberto', label: 'Processos abertos' },
  { id: 'todos', label: 'Todos' },
];

export default function Requerimentos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const requester = isRequesterRole(user);
  const [filter, setFilter] = useState(requester ? 'todos' : 'fila');
  const { data, isLoading, isError, error } = useQuery(['requerimentos'], () => processService.listRequerimentos());
  const { data: enterprises = [] } = useQuery(['req-enterprises'], () =>
    fetchAllPages((params) => identityService.getEnterprises(params))
  );
  const { data: tipos = [] } = useQuery(['req-tipos'], () =>
    fetchAllPages((params) => licenseService.getTiposLicenca(params))
  );
  const rows = useMemo(() => unwrap(data), [data]);
  const empById = useMemo(
    () => Object.fromEntries(enterprises.map((e) => [String(e.id), e.name || e.nome])),
    [enterprises]
  );
  const tipoById = useMemo(
    () => Object.fromEntries(tipos.map((t) => [String(t.id), t.nome])),
    [tipos]
  );

  const counts = useMemo(
    () => ({
      todos: rows.length,
      fila: rows.filter((r) => FILA_ORGAO.includes(r.status)).length,
      rascunho: rows.filter((r) => r.status === 'rascunho').length,
      enviado: rows.filter((r) => r.status === 'enviado').length,
      devolvido: rows.filter((r) => r.status === 'devolvido').length,
      processo_aberto: rows.filter((r) => r.status === 'processo_aberto').length,
    }),
    [rows]
  );

  const visible = useMemo(() => {
    if (filter === 'fila') return rows.filter((r) => FILA_ORGAO.includes(r.status));
    if (filter === 'todos') return rows;
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const filters = requester ? FILTERS_REQUESTER : FILTERS_ORGAO;

  return (
    <FormPageShell>
      <PageHeader
        title="Requerimentos"
        description={
          requester
            ? 'Rascunhos e pedidos de licenciamento dos seus empreendimentos. Só vira processo depois da análise do órgão.'
            : 'Pedidos enviados pelos requerentes. Analise, devolva com pendências ou abra o processo.'
        }
        breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Requerimentos' }]}
      >
        {requester ? (
          <button type="button" className="btn btn-primary inline-flex items-center gap-2" onClick={() => navigate('/requerimentos/novo')}>
            <FilePlus2 className="h-4 w-4" />
            Requerer licenciamento
          </button>
        ) : null}
      </PageHeader>

      {isError ? <QueryErrorBanner error={error} /> : null}

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {requester ? 'Meus pedidos' : 'Na fila'}
          </p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{requester ? counts.todos : counts.fila}</p>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-amber-800">Devolvidos</p>
          <p className="mt-1 text-2xl font-semibold text-amber-950">{counts.devolvido}</p>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/80 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-emerald-800">Processos abertos</p>
          <p className="mt-1 text-2xl font-semibold text-emerald-950">{counts.processo_aberto}</p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {filters.map((item) => {
          const active = filter === item.id;
          const n = counts[item.id] ?? visible.length;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setFilter(item.id)}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                active
                  ? 'border-primary-500 bg-primary-600 text-white shadow-sm'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-primary-300'
              }`}
            >
              {item.label}
              <span className={`ml-1.5 text-xs ${active ? 'text-primary-100' : 'text-slate-400'}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-5">
        {isLoading ? (
          <p className="rounded-2xl border border-slate-200 bg-white px-6 py-10 text-slate-500">Carregando requerimentos…</p>
        ) : visible.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
            <Inbox className="mx-auto mb-3 h-9 w-9 text-slate-300" />
            <p className="font-medium text-slate-800">Nenhum requerimento nesta fila</p>
            <p className="mt-1 text-sm text-slate-500">
              {requester
                ? 'Escolha um empreendimento e um tipo de licença para começar o rascunho.'
                : 'Quando um requerente enviar um pedido, ele aparece na fila do órgão.'}
            </p>
            {requester ? (
              <button type="button" className="btn btn-primary mt-4" onClick={() => navigate('/requerimentos/novo')}>
                Começar rascunho
              </button>
            ) : null}
          </div>
        ) : (
          <ul className="grid gap-3">
            {visible.map((row) => {
              const meta = requerimentoStatusMeta(row.status);
              const progress = checklistProgress(row.checklist);
              const emp = empById[String(row.empreendimento_id)] || 'Empreendimento';
              const tipo = tipoById[String(row.tipo_licenca_id)] || 'Tipo de licença';
              return (
                <li key={row.id}>
                  <Link
                    to={`/requerimentos/${row.id}`}
                    className={`block rounded-2xl border bg-white px-5 py-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${meta.chip}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-900">{row.numero}</p>
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.className}`}>
                            {meta.label}
                          </span>
                        </div>
                        <p className="mt-1 truncate text-sm text-slate-600">
                          {emp} · {tipo}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">Atualizado {formatReqDate(row.updated_at || row.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-36">
                          <div className="mb-1 flex justify-between text-[11px] text-slate-500">
                            <span>Checklist</span>
                            <span>
                              {progress.done}/{progress.total || 0}
                            </span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">
                            <div
                              className={`h-full rounded-full ${progress.pct === 100 ? 'bg-emerald-500' : 'bg-primary-500'}`}
                              style={{ width: `${progress.pct}%` }}
                            />
                          </div>
                        </div>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-primary-700">
                          {requester && row.editavel ? 'Continuar' : 'Abrir'}
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {!requester && counts.fila > 0 ? (
        <p className="mt-4 inline-flex items-center gap-2 text-sm text-slate-500">
          <ClipboardList className="h-4 w-4" />
          A fila mostra pedidos enviados e pedidos de desbloqueio aguardando o órgão.
        </p>
      ) : null}
    </FormPageShell>
  );
}
